import {
  Paper,
  Box,
  Button,
  Typography,
  Tooltip,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import moment from "moment";
import TextField from "@mui/material/TextField";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Autocomplete from "@mui/material/Autocomplete";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { updateOutstandingDebit } from "../actions/debit";
import { updateOutstandingCredit } from "../actions/credit";
import { updateSubmission } from "../actions/submission";
import { MutatingDots } from "react-loader-spinner";
import { ProgressBar } from "react-loader-spinner";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { Helmet } from "react-helmet-async";
import API from "../Api/api";
import toast from "react-hot-toast";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";

const colors = {
  primary: "#21313c",
  primaryLight: "#304753",
  primaryDark: "#19262f",
  secondary: "#17b859",
  secondaryDark: "#109347",
  secondarySoft: "rgba(23, 184, 89, 0.12)",
  surface: "#ffffff",
  surfaceSoft: "#f8fafc",
  border: "#e4e9ee",
  textPrimary: "#21313c",
  textSecondary: "#667085",
  warning: "#f5af14",
  warningSoft: "rgba(245, 175, 20, 0.14)",
  dangerSoft: "rgba(211, 47, 47, 0.08)",
};

export default function UpdateReport() {
  const locations = useSelector((state) => state.location) || [];
  const [reconciliationType, setReconciliationType] = React.useState("double");
  const [primary, setPrimary] = React.useState("head office");
  const [secondary, setSecondary] = React.useState("");
  const [singleLocation, setSingleLocation] = React.useState("");
  const [years, setYears] = React.useState([]);
  const [trigger, setTrigger] = React.useState(false);
  const [branchOutCr, setBranchOutCr] = React.useState([]);
  const [branchOutDr, setBranchOutDr] = React.useState([]);
  const [headOfficeOutCr, setHeadOfficeOutCr] = React.useState([]);
  const [headOfficeOutDr, setHeadOfficeOutDr] = React.useState([]);
  const [singleOutCr, setSingleOutCr] = React.useState([]);
  const [singleOutDr, setSingleOutDr] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [loader] = React.useState(false);
  const [fetching, setFetching] = React.useState(false);
  const [balance, setBalance] = React.useState([]);

  React.useEffect(() => {
    const branch =
      reconciliationType === "single"
        ? singleLocation
        : primary && secondary
          ? primary + " " + secondary
          : "";

    if (!branch) return;

    let fetchingId;
    const fetch = async () => {
      try {
        fetchingId = toast.loading("Fetching available dates...");
        const res = await API.get(
          `/api/v1/reconciliation-submission?branch=${branch}`,
        );
        if (res.data) {
          toast.remove(fetchingId);
          toast.success("Dates Loaded");
          setYears(res.data);
        }
      } catch (error) {
        toast.remove(fetchingId);
        toast.error(error.message);
      }
    };
    fetch();
  }, [reconciliationType, primary, secondary, singleLocation]);

  const fetchData = async () => {
    let fetchingId;
    try {
      if (reconciliationType === "single") {
        if (!singleLocation || !inputValue) {
          setTrigger(false);
          setFetching(false);
          toast.error("Please select a location and date to continue");
          return;
        }

        setFetching(true);
        fetchingId = toast.loading("Generating report....");

        const fetchSingleOutCr = await API.get(
          `/api/v1/outstanding-credit?location=primary&branch=${singleLocation}&submission_date=${inputValue}`,
        );
        const fetchSingleOutDr = await API.get(
          `/api/v1/outstanding-debit?location=primary&branch=${singleLocation}&submission_date=${inputValue}`,
        );
        const balance = await API.get(
          `/api/v1/reconciliation-submission?branch=${singleLocation}&submission_date=${inputValue}`,
        );

        setBalance(balance.data);
        setSingleOutCr(fetchSingleOutCr.data);
        setSingleOutDr(fetchSingleOutDr.data);
        setTrigger(true);
        setFetching(false);

        toast.remove(fetchingId);
        toast.success("Data Loaded");
        return;
      }

      if (primary && secondary && inputValue) {
        setFetching(true);
        fetchingId = toast.loading("Generating report....");

        const fetchBranchOutCr = await API.get(
          `/api/v1/outstanding-credit?location=secondary&branch=${
            secondary + " " + primary
          }&submission_date=${inputValue}`,
        );
        const fetchBranchOutDr = await API.get(
          `/api/v1/outstanding-debit?location=secondary&branch=${
            secondary + " " + primary
          }&submission_date=${inputValue}`,
        );
        const fetchHeadOfficeOutCr = await API.get(
          `/api/v1/outstanding-credit?location=primary&branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );
        const fetchHeadOfficeOutDr = await API.get(
          `/api/v1/outstanding-debit?location=primary&branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );
        const balance = await API.get(
          `/api/v1/reconciliation-submission?branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );

        setBalance(balance.data);
        setBranchOutCr(fetchBranchOutCr.data);
        setBranchOutDr(fetchBranchOutDr.data);
        setHeadOfficeOutCr(fetchHeadOfficeOutCr.data);
        setHeadOfficeOutDr(fetchHeadOfficeOutDr.data);
        setTrigger(true);
        setFetching(false);

        toast.remove(fetchingId);
        toast.success("Data Loaded");
      } else {
        setTrigger(false);
        setFetching(false);
        toast.remove(fetchingId);
        toast.error(
          "Please select primary location,secondary location,and date to continue",
        );
      }
    } catch (error) {
      setFetching(false);
      toast.remove(fetchingId);
      toast.error(error.message);
    }
  };

  const handleChangePrimary = (event) => {
    setPrimary(event.target.value);
  };

  const handleChangeSecondary = (event) => {
    setSecondary(event.target.value);
  };

  const handleChangeSingleLocation = (event) => {
    setSingleLocation(event.target.value);
  };

  const handleChangeReconciliationType = (event, newType) => {
    if (!newType) return;
    setReconciliationType(newType);
    setTrigger(false);
    setInputValue("");
    setYears([]);
  };

  const accountType = [];
  const branchBalance = [];
  const headOfficeBalance = [];
  const ourOutStandingCredit = [];
  const theirOutStandingCredit = [];
  const ourOutStandingDebit = [];
  const theirOutStandingDebit = [];

  branchOutCr.forEach((d) => {
    accountType.push(d.account_type);
    branchBalance.push(d.balance);
    theirOutStandingCredit.push(d.credit_amt);
  });

  branchOutDr.forEach((d) => {
    accountType.push(d.account_type);
    branchBalance.push(d.balance);
    theirOutStandingDebit.push(d.debit_amt);
  });

  headOfficeOutDr.forEach((d) => {
    accountType.push(d.account_type);
    headOfficeBalance.push(d.balance);
    ourOutStandingDebit.push(d.debit_amt);
  });

  headOfficeOutCr.forEach((d) => {
    accountType.push(d.account_type);
    headOfficeBalance.push(d.balance);
    ourOutStandingCredit.push(d.credit_amt);
  });

  const ourOutStandingCreditTotal = ourOutStandingCredit.reduce(
    (acc, val) => acc + val,
    0,
  );
  const theirOutStandingCreditTotal = theirOutStandingCredit.reduce(
    (acc, val) => acc + val,
    0,
  );
  const ourOutStandingDebitTotal = ourOutStandingDebit.reduce(
    (acc, val) => acc + val,
    0,
  );
  const theirOutStandingDebitTotal = theirOutStandingDebit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const singleOutCreditTotal = singleOutCr.reduce(
    (acc, val) => acc + val.credit_amt,
    0,
  );
  const singleOutDebitTotal = singleOutDr.reduce(
    (acc, val) => acc + val.debit_amt,
    0,
  );

  function analyData(label) {
    return {
      label,
    };
  }

  const dates = years
    .map((data) => analyData(moment(data.submission_date).format("YYYY-MM-DD")))
    .reverse();

  function ageing(date1, date2) {
    const formatDate1 = moment(date1).format("YYYY-MM-DD");
    const formatDate2 = moment(date2).format("YYYY-MM-DD");
    const diff = moment(formatDate2).diff(moment(formatDate1), "days");
    return diff;
  }

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
  });

  const [editingId, setEditingId] = React.useState(null);

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const dispatch = useDispatch();

  const handleSaveDebit = (id, updatedData) => {
    dispatch(updateOutstandingDebit(id, updatedData));
    const branch =
      reconciliationType === "single"
        ? singleLocation
        : primary + " " + secondary;
    dispatch(
      updateSubmission(branch, years[0].submission_date, {
        approved: "false",
        status: "reconciliation was updated",
      }),
    );
    setEditingId(null);
  };

  const handleSaveCredit = (id, updatedData) => {
    dispatch(updateOutstandingCredit(id, updatedData));
    setEditingId(null);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: colors.surfaceSoft,
      fontSize: "0.9rem",
      "&.Mui-focused fieldset": {
        borderColor: colors.secondary,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: colors.secondary,
    },
  };

  const renderEditableRow = (el, index, arr, setArr, amountKey, onSave) => (
    <TableRow
      key={el.id || el._id}
      hover
      sx={{
        "&:nth-of-type(odd)": {
          backgroundColor: "#fcfdff",
        },
      }}
    >
      <TableCell>
        {el._id === editingId ? (
          <TextField
            size="small"
            type="date"
            value={moment(el.value_date).format("YYYY-MM-DD")}
            onChange={(e) =>
              setArr((prevData) =>
                prevData.map((item) =>
                  item._id === el._id
                    ? { ...item, value_date: e.target.value }
                    : item,
                ),
              )
            }
            sx={{ ...inputSx, minWidth: 145 }}
          />
        ) : (
          moment(el.value_date).format("DD-MM-YYYY")
        )}
      </TableCell>

      <TableCell>
        {el._id === editingId ? (
          <TextField
            size="small"
            fullWidth
            value={el.description}
            onChange={(e) =>
              setArr((prevData) =>
                prevData.map((item) =>
                  item._id === el._id
                    ? { ...item, description: e.target.value }
                    : item,
                ),
              )
            }
            sx={inputSx}
          />
        ) : (
          el.description
        )}
      </TableCell>

      <TableCell>{currencyFormatter.format(el[amountKey])}</TableCell>
      <TableCell />
      <TableCell>{ageing(el.value_date, el.submission_date)}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {editingId === el._id ? (
            <Tooltip title="Save">
              <IconButton
                size="small"
                onClick={() => onSave(el._id, arr[index])}
                sx={{
                  color: colors.secondary,
                  backgroundColor: colors.secondarySoft,
                  "&:hover": {
                    backgroundColor: "rgba(23,184,89,0.2)",
                  },
                }}
              >
                <SaveRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEdit(el._id)}
                sx={{
                  color: colors.primary,
                  backgroundColor: "rgba(33,49,60,0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(33,49,60,0.14)",
                  },
                }}
              >
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {editingId === el._id && (
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                onClick={() => setEditingId(null)}
                sx={{
                  color: "#d32f2f",
                  backgroundColor: colors.dangerSoft,
                  "&:hover": {
                    backgroundColor: "rgba(211,47,47,0.16)",
                  },
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      <Helmet>
        <title>Update Report | Reconciliation</title>
      </Helmet>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
          spacing={2}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={reconciliationType}
            onChange={handleChangeReconciliationType}
            sx={{
              alignSelf: "flex-start",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px !important",
                px: 2,
              },
            }}
          >
            <ToggleButton value="double">Two Statements</ToggleButton>
            <ToggleButton value="single">Single Statement</ToggleButton>
          </ToggleButtonGroup>

          <Box
            sx={{ flex: 1, minWidth: { xs: "100%", md: 600 } }}
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              lg:
                reconciliationType === "single"
                  ? "1fr 1.2fr auto"
                  : "1fr 1fr 1.2fr auto",
            }}
            gap={2}
            alignItems="center"
          >
            {reconciliationType === "single" ? (
              <FormControl fullWidth size="small">
                <InputLabel id="single-location-label">Location</InputLabel>
                <Select
                  labelId="single-location-label"
                  value={singleLocation}
                  label="Location"
                  onChange={handleChangeSingleLocation}
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: colors.surfaceSoft,
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {locations?.map((data) => (
                    <MenuItem key={data._id} value={data.name}>
                      {data.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel id="primary-location-label">
                    Primary Location
                  </InputLabel>
                  <Select
                    labelId="primary-location-label"
                    value={primary}
                    label="Primary Location"
                    onChange={handleChangePrimary}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: colors.surfaceSoft,
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {locations?.map((data) => (
                      <MenuItem key={data._id} value={data.name}>
                        {data.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="secondary-location-label">
                    Secondary Location
                  </InputLabel>
                  <Select
                    labelId="secondary-location-label"
                    value={secondary}
                    label="Secondary Location"
                    onChange={handleChangeSecondary}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: colors.surfaceSoft,
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {locations?.map((data) => (
                      <MenuItem key={data._id} value={data.name}>
                        {data.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {loader ? (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 56,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: colors.textSecondary }}
                >
                  searching for available dates
                </Typography>
                <MutatingDots
                  height="60"
                  width="90"
                  color={colors.warning}
                  secondaryColor={colors.primary}
                  radius="12.5"
                  ariaLabel="mutating-dots-loading"
                  visible={true}
                />
              </Box>
            ) : (
              <Autocomplete
                size="small"
                disablePortal
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                id="available-dates"
                options={dates}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField {...params} label="Available Dates" sx={inputSx} />
                )}
              />
            )}

            {fetching ? (
              <Box
                sx={{
                  minWidth: 80,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ProgressBar
                  height="60"
                  width="60"
                  ariaLabel="progress-bar-loading"
                  wrapperClass="progress-bar-wrapper"
                  borderColor={colors.primary}
                  barColor={colors.warning}
                />
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={fetchData}
                startIcon={<CompareArrowsRoundedIcon />}
                sx={{
                  textTransform: "none",
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                  color: "#fff",
                  borderRadius: "12px",
                  fontWeight: 700,
                  height: 40,
                  px: 2.5,
                  boxShadow: "0 10px 18px rgba(33,49,60,0.18)",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primary} 100%)`,
                  },
                }}
                size="small"
              >
                Fetch Data
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      {trigger ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            overflow: "hidden",
          }}
        >
          <TableContainer component={Paper} elevation={0}>
            <Table
              sx={{ minWidth: 900 }}
              aria-label="update reconciliation table"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                      color: "#fff",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      textAlign: "center",
                      py: 1.2,
                      borderBottom: "none",
                    }}
                  >
                    {reconciliationType === "single"
                      ? `Single Account Reconciliation for ${
                          singleLocation ? singleLocation : "[Location Name]"
                        }`
                      : `Reconciliation Statement with ${
                          secondary ? secondary : "[Branch Name]"
                        }`}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      backgroundColor: "#f0f4f8",
                      color: colors.textPrimary,
                      fontWeight: 700,
                      textAlign: "center",
                      py: 0.8,
                    }}
                  >
                    AS AT : {inputValue ? inputValue : "[RECONCILIATION DATE]"}
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{
                    background: `linear-gradient(135deg, ${colors.warning} 0%, #f7c85b 100%)`,
                  }}
                >
                  <TableCell sx={{ fontWeight: 800, color: colors.primary }}>
                    DATE
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: colors.primary }}>
                    DESCRIPTION
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: colors.primary }}>
                    OUTSTANDING
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: colors.primary }}>
                    BALANCE
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: colors.primary }}>
                    AGING
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: colors.primary }}>
                    ACTION
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {reconciliationType !== "single" && (
                  <>
                <TableRow sx={{ backgroundColor: "#fcfdff" }}>
                  <TableCell />
                  <TableCell
                    sx={{ textTransform: "uppercase", fontWeight: 700 }}
                  >
                    Balance as per their books
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, color: colors.textPrimary }}
                  >
                    {balance[0] &&
                      currencyFormatter.format(balance[0].secondary_balance)}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>

                <TableRow>
                  <TableCell sx={{ color: "#98a2b3" }}>----------</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell sx={{ color: "#98a2b3" }}>---------</TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: "#fafbfc" }}>
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    {accountType[0] === "credit account" ? "LESS" : "ADD"}: OUR
                    OUTSTANDING CREDIT
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>

                {headOfficeOutCr.map((el, index) => (
                  <TableRow
                    key={el.id || el._id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "#fcfdff",
                      },
                    }}
                  >
                    <TableCell>
                      {el._id === editingId ? (
                        <TextField
                          size="small"
                          type="date"
                          value={moment(el.value_date).format("YYYY-MM-DD")}
                          onChange={(e) =>
                            setHeadOfficeOutCr((prevData) =>
                              prevData.map((item) =>
                                item._id === el._id
                                  ? { ...item, value_date: e.target.value }
                                  : item,
                              ),
                            )
                          }
                          sx={{ ...inputSx, minWidth: 145 }}
                        />
                      ) : (
                        moment(el.value_date).format("DD-MM-YYYY")
                      )}
                    </TableCell>

                    <TableCell>
                      {el._id === editingId ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={el.description}
                          onChange={(e) =>
                            setHeadOfficeOutCr((prevData) =>
                              prevData.map((item) =>
                                item._id === el._id
                                  ? { ...item, description: e.target.value }
                                  : item,
                              ),
                            )
                          }
                          sx={inputSx}
                        />
                      ) : (
                        el.description
                      )}
                    </TableCell>

                    <TableCell>
                      {currencyFormatter.format(el.credit_amt)}
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      {ageing(el.value_date, el.submission_date)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {editingId === el._id ? (
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleSaveCredit(el._id, headOfficeOutCr[index])
                              }
                              sx={{
                                color: colors.secondary,
                                backgroundColor: colors.secondarySoft,
                                "&:hover": {
                                  backgroundColor: "rgba(23,184,89,0.2)",
                                },
                              }}
                            >
                              <SaveRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(el._id)}
                              sx={{
                                color: colors.primary,
                                backgroundColor: "rgba(33,49,60,0.08)",
                                "&:hover": {
                                  backgroundColor: "rgba(33,49,60,0.14)",
                                },
                              }}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {editingId === el._id && (
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={() => setEditingId(null)}
                              sx={{
                                color: "#d32f2f",
                                backgroundColor: colors.dangerSoft,
                                "&:hover": {
                                  backgroundColor: "rgba(211,47,47,0.16)",
                                },
                              }}
                            >
                              <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell sx={{ color: "#98a2b3" }}>----------</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    {currencyFormatter.format(ourOutStandingCreditTotal)}
                  </TableCell>
                  <TableCell />
                  <TableCell sx={{ color: "#98a2b3" }}>---------</TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: "#fafbfc" }}>
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    {accountType[0] === "credit account" ? "LESS" : "ADD"}:
                    THEIR OUTSTANDING CREDIT
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>

                {branchOutCr.map((el, index) => (
                  <TableRow
                    key={el.id || el._id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "#fcfdff",
                      },
                    }}
                  >
                    <TableCell>
                      {el._id === editingId ? (
                        <TextField
                          size="small"
                          type="date"
                          value={moment(el.value_date).format("YYYY-MM-DD")}
                          onChange={(e) =>
                            setBranchOutCr((prevData) =>
                              prevData.map((item) =>
                                item._id === el._id
                                  ? { ...item, value_date: e.target.value }
                                  : item,
                              ),
                            )
                          }
                          sx={{ ...inputSx, minWidth: 145 }}
                        />
                      ) : (
                        moment(el.value_date).format("DD-MM-YYYY")
                      )}
                    </TableCell>

                    <TableCell>
                      {el._id === editingId ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={el.description}
                          onChange={(e) =>
                            setBranchOutCr((prevData) =>
                              prevData.map((item) =>
                                item._id === el._id
                                  ? { ...item, description: e.target.value }
                                  : item,
                              ),
                            )
                          }
                          sx={inputSx}
                        />
                      ) : (
                        el.description
                      )}
                    </TableCell>

                    <TableCell>
                      {currencyFormatter.format(el.credit_amt)}
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      {ageing(el.value_date, el.submission_date)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {editingId === el._id ? (
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleSaveCredit(el._id, branchOutCr[index])
                              }
                              sx={{
                                color: colors.secondary,
                                backgroundColor: colors.secondarySoft,
                                "&:hover": {
                                  backgroundColor: "rgba(23,184,89,0.2)",
                                },
                              }}
                            >
                              <SaveRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(el._id)}
                              sx={{
                                color: colors.primary,
                                backgroundColor: "rgba(33,49,60,0.08)",
                                "&:hover": {
                                  backgroundColor: "rgba(33,49,60,0.14)",
                                },
                              }}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {editingId === el._id && (
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={() => setEditingId(null)}
                              sx={{
                                color: "#d32f2f",
                                backgroundColor: colors.dangerSoft,
                                "&:hover": {
                                  backgroundColor: "rgba(211,47,47,0.16)",
                                },
                              }}
                            >
                              <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell sx={{ color: "#98a2b3" }}>----------</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    {currencyFormatter.format(theirOutStandingCreditTotal)}
                  </TableCell>
                  <TableCell />
                  <TableCell sx={{ color: "#98a2b3" }}>---------</TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: "#fafbfc" }}>
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    {accountType[0] === "credit account" ? "ADD" : "LESS"} : OUR
                    OUTSTANDING DEBIT
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>

                {headOfficeOutDr.map((el, index) => (
                  <TableRow
                    key={el.id || el._id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "#fcfdff",
                      },
                    }}
                  >
                    <TableCell>
                      {el._id === editingId ? (
                        <TextField
                          size="small"
                          type="date"
                          value={moment(el.value_date).format("YYYY-MM-DD")}
                          onChange={(e) =>
                            setHeadOfficeOutDr((prevData) =>
                              prevData.map((item) =>
                                item._id === el._id
                                  ? { ...item, value_date: e.target.value }
                                  : item,
                              ),
                            )
                          }
                          sx={{ ...inputSx, minWidth: 145 }}
                        />
                      ) : (
                        moment(el.value_date).format("DD-MM-YYYY")
                      )}
                    </TableCell>

                    <TableCell>
                      {el._id === editingId ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={el.description}
                          onChange={(e) =>
                            setHeadOfficeOutDr((prevData) =>
                              prevData.map((item) =>
                                item._id === el._id
                                  ? { ...item, description: e.target.value }
                                  : item,
                              ),
                            )
                          }
                          sx={inputSx}
                        />
                      ) : (
                        el.description
                      )}
                    </TableCell>

                    <TableCell>
                      {currencyFormatter.format(el.debit_amt)}
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      {ageing(el.value_date, el.submission_date)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {editingId === el._id ? (
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleSaveDebit(el._id, headOfficeOutDr[index])
                              }
                              sx={{
                                color: colors.secondary,
                                backgroundColor: colors.secondarySoft,
                                "&:hover": {
                                  backgroundColor: "rgba(23,184,89,0.2)",
                                },
                              }}
                            >
                              <SaveRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(el._id)}
                              sx={{
                                color: colors.primary,
                                backgroundColor: "rgba(33,49,60,0.08)",
                                "&:hover": {
                                  backgroundColor: "rgba(33,49,60,0.14)",
                                },
                              }}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {editingId === el._id && (
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={() => setEditingId(null)}
                              sx={{
                                color: "#d32f2f",
                                backgroundColor: colors.dangerSoft,
                                "&:hover": {
                                  backgroundColor: "rgba(211,47,47,0.16)",
                                },
                              }}
                            >
                              <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell sx={{ color: "#98a2b3" }}>----------</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    {currencyFormatter.format(ourOutStandingDebitTotal)}
                  </TableCell>
                  <TableCell />
                  <TableCell sx={{ color: "#98a2b3" }}>---------</TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: "#fafbfc" }}>
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    {accountType[0] === "credit account" ? "ADD" : "LESS"} :
                    THEIR OUTSTANDING DEBIT
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>

                {branchOutDr.map((el, index) => (
                  <TableRow
                    key={el.id || el._id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "#fcfdff",
                      },
                    }}
                  >
                    <TableCell>
                      {el._id === editingId ? (
                        <TextField
                          size="small"
                          type="date"
                          value={moment(el.value_date).format("YYYY-MM-DD")}
                          onChange={(e) =>
                            setBranchOutDr((prevData) =>
                              prevData.map((item) =>
                                item._id === el._id
                                  ? { ...item, value_date: e.target.value }
                                  : item,
                              ),
                            )
                          }
                          sx={{ ...inputSx, minWidth: 145 }}
                        />
                      ) : (
                        moment(el.value_date).format("DD-MM-YYYY")
                      )}
                    </TableCell>

                    <TableCell>
                      {el._id === editingId ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={el.description}
                          onChange={(e) =>
                            setBranchOutDr((prevData) =>
                              prevData.map((item) =>
                                item._id === el._id
                                  ? { ...item, description: e.target.value }
                                  : item,
                              ),
                            )
                          }
                          sx={inputSx}
                        />
                      ) : (
                        el.description
                      )}
                    </TableCell>

                    <TableCell>
                      {currencyFormatter.format(el.debit_amt)}
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      {ageing(el.value_date, el.submission_date)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {editingId === el._id ? (
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleSaveDebit(el._id, branchOutDr[index])
                              }
                              sx={{
                                color: colors.secondary,
                                backgroundColor: colors.secondarySoft,
                                "&:hover": {
                                  backgroundColor: "rgba(23,184,89,0.2)",
                                },
                              }}
                            >
                              <SaveRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(el._id)}
                              sx={{
                                color: colors.primary,
                                backgroundColor: "rgba(33,49,60,0.08)",
                                "&:hover": {
                                  backgroundColor: "rgba(33,49,60,0.14)",
                                },
                              }}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {editingId === el._id && (
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={() => setEditingId(null)}
                              sx={{
                                color: "#d32f2f",
                                backgroundColor: colors.dangerSoft,
                                "&:hover": {
                                  backgroundColor: "rgba(211,47,47,0.16)",
                                },
                              }}
                            >
                              <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell sx={{ color: "#98a2b3" }}>----------</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    {currencyFormatter.format(theirOutStandingDebitTotal)}
                  </TableCell>
                  <TableCell sx={{ color: "#98a2b3" }}>---------</TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: "#fcfdff" }}>
                  <TableCell />
                  <TableCell
                    sx={{ fontWeight: 800, color: colors.textPrimary }}
                  >
                    BALANCE AS PER OUR BOOKS
                  </TableCell>
                  <TableCell />
                  {balance[0] && (
                    <TableCell
                      sx={{ fontWeight: 800, color: colors.textPrimary }}
                    >
                      {currencyFormatter.format(balance[0].primary_balance)}
                    </TableCell>
                  )}
                  <TableCell />
                  <TableCell />
                </TableRow>
                  </>
                )}

                {reconciliationType === "single" && (
                  <>
                    <TableRow sx={{ backgroundColor: "#fcfdff" }}>
                      <TableCell />
                      <TableCell
                        sx={{ textTransform: "uppercase", fontWeight: 700 }}
                      >
                        Balance at period start
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: colors.textPrimary }}
                      >
                        {balance[0] &&
                          currencyFormatter.format(
                            balance[0].secondary_balance,
                          )}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ color: "#98a2b3" }}>
                        ----------
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell sx={{ color: "#98a2b3" }}>
                        ---------
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ backgroundColor: "#fafbfc" }}>
                      <TableCell />
                      <TableCell
                        sx={{ fontWeight: 800, color: colors.textPrimary }}
                      >
                        ADD: OUTSTANDING CREDIT
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>

                    {singleOutCr.map((el, index) =>
                      renderEditableRow(
                        el,
                        index,
                        singleOutCr,
                        setSingleOutCr,
                        "credit_amt",
                        handleSaveCredit,
                      ),
                    )}

                    <TableRow>
                      <TableCell sx={{ color: "#98a2b3" }}>
                        ----------
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell
                        sx={{ fontWeight: 800, color: colors.textPrimary }}
                      >
                        {currencyFormatter.format(singleOutCreditTotal)}
                      </TableCell>
                      <TableCell />
                      <TableCell sx={{ color: "#98a2b3" }}>
                        ---------
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ backgroundColor: "#fafbfc" }}>
                      <TableCell />
                      <TableCell
                        sx={{ fontWeight: 800, color: colors.textPrimary }}
                      >
                        LESS: OUTSTANDING DEBIT
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>

                    {singleOutDr.map((el, index) =>
                      renderEditableRow(
                        el,
                        index,
                        singleOutDr,
                        setSingleOutDr,
                        "debit_amt",
                        handleSaveDebit,
                      ),
                    )}

                    <TableRow>
                      <TableCell sx={{ color: "#98a2b3" }}>
                        ----------
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell
                        sx={{ fontWeight: 800, color: colors.textPrimary }}
                      >
                        {currencyFormatter.format(singleOutDebitTotal)}
                      </TableCell>
                      <TableCell />
                      <TableCell sx={{ color: "#98a2b3" }}>
                        ---------
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ backgroundColor: "#fcfdff" }}>
                      <TableCell />
                      <TableCell
                        sx={{ fontWeight: 800, color: colors.textPrimary }}
                      >
                        COMPUTED BALANCE AT PERIOD END
                      </TableCell>
                      <TableCell />
                      {balance[0] && (
                        <TableCell
                          sx={{ fontWeight: 800, color: colors.textPrimary }}
                        >
                          {currencyFormatter.format(
                            balance[0].primary_balance,
                          )}
                        </TableCell>
                      )}
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Box>
          <Paper
            elevation={0}
            sx={{
              minHeight: "58vh",
              borderRadius: "22px",
              border: "1px dashed #cbd5df",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
            }}
          >
            <Box sx={{ textAlign: "center", maxWidth: 760 }}>
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "24px",
                  background: colors.secondarySoft,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EditNoteRoundedIcon
                  sx={{ fontSize: 40, color: colors.secondary }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: colors.textPrimary,
                  mb: 1,
                }}
              >
                No Report Loaded
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.8,
                  fontSize: "1rem",
                }}
              >
                {reconciliationType === "single"
                  ? "Select a location and available reconciliation date, then fetch the report to begin editing."
                  : "Select your primary location, secondary location, and available reconciliation date, then fetch the report to begin editing."}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
